import { Overlay } from '@angular/cdk/overlay';
import { Component, Inject, Injectable, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FlightSegmentSch, OriginDestinationOption } from '../models/OTAAirScheduleResponse';
import { connectionWait, formatDuration } from '../flight-ticket-frame/flight-ticket.util';

const SHEET_MAX_WIDTH = '(max-width: 550px)';

/**
 * One details surface for every result. Mobile opens it as a bottom sheet; desktop as a
 * right drawer over a light backdrop (click outside, Escape and the X all close it).
 * A second "Ver detalhes" replaces the flight instead of stacking another panel.
 */
@Injectable({ providedIn: 'root' })
export class FlightDetailsPanelService {
  private readonly dialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private ref: MatDialogRef<FlightDetailsDialogComponent> | null = null;
  readonly flight = signal<OriginDestinationOption | null>(null);

  show(flight: OriginDestinationOption): void {
    this.flight.set(flight);
    if (this.ref) return;

    const sheet = typeof window !== 'undefined' && !!window.matchMedia?.(SHEET_MAX_WIDTH).matches;
    this.ref = this.dialog.open(FlightDetailsDialogComponent, {
      data: { flight } satisfies FlightDetailsDialogData,
      autoFocus: 'dialog',
      restoreFocus: true,
      ...(sheet
        ? {
            panelClass: ['flight-details-panel', 'flight-details-panel--sheet'],
            position: { bottom: '0' },
            width: '100vw',
            maxWidth: '100vw',
            maxHeight: '85vh'
          }
        : {
            panelClass: ['flight-details-panel', 'flight-details-panel--drawer'],
            backdropClass: 'flight-details-backdrop',
            position: { top: '0', right: '0' },
            height: '100vh',
            width: '400px',
            maxWidth: '92vw',
            maxHeight: '100vh',
            hasBackdrop: true,
            scrollStrategy: this.overlay.scrollStrategies.noop()
          })
    });
    this.ref.afterClosed().subscribe(() => this.reset());
  }

  reset(): void {
    this.ref = null;
    this.flight.set(null);
  }
}

export interface FlightDetailsDialogData {
  flight: OriginDestinationOption;
}

export interface FlightDetailsLeg {
  segment: FlightSegmentSch;
  flightNumber: string;
  originCode: string;
  originCity: string;
  departureTime: string;
  destinationCode: string;
  destinationCity: string;
  arrivalTime: string;
  arrivesNextDay: boolean;
  duration: string;
  /** Label of the stop before the next leg ("Conexão · 1h 15min", or "Conexão" when the wait cannot be computed). */
  connection: string | null;
}

/**
 * Vertical itinerary of a search result, opened from "Ver detalhes".
 * Presentation only: every value comes straight from the flight segments.
 */
@Component({
  selector: 'app-flight-details-dialog',
  templateUrl: './flight-details-dialog.component.html',
  styleUrls: ['./flight-details-dialog.component.scss'],
  standalone: false
})
export class FlightDetailsDialogComponent {
  private readonly panel = inject(FlightDetailsPanelService);

  constructor(@Inject(MAT_DIALOG_DATA) public readonly data: FlightDetailsDialogData) {}

  /** The open panel follows the latest flight; tests that pass only dialog data keep working. */
  get flight(): OriginDestinationOption | undefined {
    return this.panel.flight() ?? this.data?.flight;
  }

  get legs(): FlightDetailsLeg[] {
    const segments = this.flight?.flightSegments ?? [];
    return segments.map((segment, index) => this.toLeg(segment, segments[index + 1]));
  }

  get routeLabel(): string {
    if (!this.legs.length) return '';
    return `${this.legs[0].originCode} → ${this.legs[this.legs.length - 1].destinationCode}`;
  }

  get flightNumbers(): string {
    return this.legs.map(leg => `LA${leg.flightNumber}`).join(' / ');
  }

  private toLeg(segment: FlightSegmentSch, next?: FlightSegmentSch): FlightDetailsLeg {
    const connection = next
      ? connectionWait(segment.arrivalDateTime, segment.destinationLocationCode, next.departureDateTime, next.originLocationCode)
      : null;

    return {
      segment,
      flightNumber: segment.flightNumber,
      originCode: segment.originLocationCode,
      originCity: segment.originCityName?.trim() ?? '',
      departureTime: clockOf(segment.departureDateTime),
      destinationCode: segment.destinationLocationCode,
      destinationCity: segment.destinationCityName?.trim() ?? '',
      arrivalTime: clockOf(segment.arrivalDateTime),
      arrivesNextDay: dayOf(segment.departureDateTime) !== dayOf(segment.arrivalDateTime),
      duration: formatDuration(segment.duration),
      connection: next ? (connection ? `Conexão · ${connection}` : 'Conexão') : null
    };
  }
}

function clockOf(value: string | null | undefined): string {
  return value && value.length >= 16 ? value.slice(11, 16) : '';
}

function dayOf(value: string | null | undefined): string {
  return value?.slice(0, 10) ?? '';
}
